import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Product {
  name: string;
  thumbnail: string;
  productId: string;
  variantId: string;
  price: number;
  discountedPrice: number;
  color?: string;
  size?: string;
  weight?: string;
  quantity: number;
  vendorId: string;
}

interface AddProductToVendor {
  vendorId: string;
  products: Product;
  totalAmount: number;
  totalAmountAfterDiscount: number;
  coupon?: {
    code: string;
    discountAmount: number;
  };
}

interface UpdateProductInVendor {
  vendorId: string;
  productId: string;
  quantity: number;
  variantId: string;
}
interface UpdateCoupon {
  vendorId: string;
  code: string;
  discount: number;
}

export interface CartProduct {
  vendorId: string;
  products: Product[];
  totalAmount: number;
  totalAmountAfterDiscount?: number;
  coupon?: {
    code: string;
    discountAmount: number;
  };
}

interface CartState {
  cart: CartProduct[];
}

const initialState: CartState = {
  cart: [] as CartProduct[]
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCart: (state, action: PayloadAction<CartProduct[]>) => {
      state.cart = action.payload;
    },
    addProductsToVendor: (state, action: PayloadAction<AddProductToVendor>) => {
      const index = state.cart.findIndex(
        product => product.vendorId === action.payload.vendorId
      );
      if (index !== -1) {
        const existingProduct = state.cart[index].products.find(
          product =>
            product.productId === action.payload.products.productId &&
            product.variantId === action.payload.products.variantId
        );
        if (existingProduct) {
          existingProduct.quantity += action.payload.products.quantity;
        } else {
          state.cart[index].products.push(action.payload.products);
        }
        state.cart[index].totalAmount += action.payload.totalAmount;
      } else {
        state.cart.push({
          // coupon: action.payload.coupon,
          totalAmountAfterDiscount: action.payload.totalAmountAfterDiscount,
          vendorId: action.payload.vendorId,
          products: [action.payload.products],
          totalAmount: action.payload.totalAmount
        });
      }
    },
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.cart = state.cart.filter(item => item.vendorId !== action.payload);
    },
    updateCart: (state, action: PayloadAction<UpdateProductInVendor>) => {
      const { vendorId, productId, variantId, quantity } = action.payload;
      const index = state.cart.findIndex(item => item.vendorId === vendorId);
      if (index !== -1) {
        const productIndex = state.cart[index].products.findIndex(
          product =>
            product.productId === productId && product.variantId === variantId
        );
        if (productIndex !== -1) {
          state.cart[index].products[productIndex].quantity = quantity;
          state.cart[index].totalAmount = state.cart[index].products.reduce(
            (total, product) => total + product.price * product.quantity,
            0
          );
        }
      }
    },
    removeProductFromVendor: (state, action) => {
      const { vendorId, productId, variantId } = action.payload;
      const index = state.cart.findIndex(
        product => product.vendorId === vendorId
      );
      if (index !== -1) {
        const productIndex = state.cart[index].products.findIndex(
          product =>
            product.productId === productId && product.variantId === variantId
        );
        if (productIndex !== -1) {
          const productPrice =
            state.cart[index].products[productIndex].price *
            state.cart[index].products[productIndex].quantity;
          state.cart[index].products.splice(productIndex, 1);
          state.cart[index].totalAmount -= productPrice;
        }
      }

      if (state.cart[index].products.length === 0) {
        state.cart.splice(index, 1);
      }
    },
    applyCouponToVendor: (state, action: PayloadAction<UpdateCoupon>) => {
      const v = state.cart.find(
        c => c.vendorId.toString() === action.payload.vendorId.toString()
      );
      if (!v) {
        console.warn('No vendor cart matched, reducer skipped');
        return;
      }

      v.coupon = {
        code: action.payload.code,
        discountAmount: Number(action.payload.discount) || 0
      };
      v.totalAmountAfterDiscount = Math.max(
        0,
        v.totalAmount - v.coupon.discountAmount
      );
      v.totalAmount = Math.max(v.totalAmount);
    },

    removeCouponFromVendor: (
      state,
      action: PayloadAction<{ vendorId: string }>
    ) => {
      const v = state.cart.find(c => c.vendorId === action.payload.vendorId);
      if (!v || !v.coupon) return;

      delete v.coupon;
      v.totalAmountAfterDiscount = undefined; // or = v.totalAmount
    }
  }
});

export const {
  setCart,
  addProductsToVendor,
  removeFromCart,
  updateCart,
  removeProductFromVendor,
  applyCouponToVendor,
  removeCouponFromVendor
} = cartSlice.actions;
export default cartSlice.reducer;
